// A card under "Ready to merge": the issue, every other issue its proposal
// carries (Poulpe-554), its proposal's proof (its check,
// its reviewer, whether this person looked), its files from the kept copy,
// and the copy's diff, opened in the card in the Changes pane's look. At the
// diff's foot, the review's evidence and the person's own review: Looks good
// (look_at_proposal), or Ask for changes… with a note (return_proposal),
// which sends the work back to its session. A link to its proposal
// (`#proposal-<id>`, queue/words.ts) opens its diff.
// A card held after its check failed folds what the failed part said under
// "Check output", closed until opened.
// A ready card has a box to pick it for Merge together, with its place in
// the order picked; one held by a batch that stopped says why, and can be
// picked again. A card in a batch that runs or waits says where it is, and
// has neither box nor diff: its approval, if asked, is in the strip.
// While a person reviews the project's proposals (Poulpe-688), a card
// awaiting their verdict has no box: its diff's foot has Approve, and Ask
// for changes… with a note, each a verdict (review_verdict) for the exact
// head shown.
//
// An urgent proposal shows a badge, whose title says who marked it, why,
// and what urgency does (Poulpe-674).
import { LiveChecks } from '../checks/progress'
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { CheckOutput, Issue, Landing, Proposal, SessionChanges } from '../api/types'
import { RETURN_NOTE_MAX } from '../api/types'
import { ChangedFiles, useFileDiffs } from '../code/ChangesPane'
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, MergeIcon } from '../components/Icons'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { longAge } from '../components/Time'
import { toast } from '../components/toasts'
import { Working } from '../components/Working'
import { useSessionPath } from '../projects/ProjectRoute'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { cx } from '../transcript/bits'
import { actorName } from '../vocab'
import { CarriedIssues } from '../queue/Carried'
import { UrgencyBadge } from '../queue/Urgency'
import { carriedKeys, proposalAnchor, short } from '../queue/words'
import { CardBody } from './Card'
import { heldText, inMergeText, keysText } from './merge'
import type { Overlap } from './ready'
import type { CopyState } from './useProposalCopies'

const ENGINES: Record<string, string> = { anthropic: 'claude', openai: 'codex', local: 'local' }

/** A count of files, and their lines added and removed. */
function Counts({ changes }: { changes: SessionChanges }) {
  const added = changes.files.reduce((sum, file) => sum + (file.additions ?? 0), 0)
  const removed = changes.files.reduce((sum, file) => sum + (file.deletions ?? 0), 0)
  const n = changes.files.length
  return <><span>{n} {n === 1 ? 'file' : 'files'}</span> <span className="changes-counts"><span className="changes-added">+{added}</span> <span className="changes-removed">−{removed}</span></span></>
}

export interface ReadyCardProps {
  issue: Issue
  proposal: Proposal
  copy: CopyState | undefined
  overlaps: readonly Overlap[]
  /** The batch it is in, running or waiting. */
  merge?: Landing | null
  /** The batch that holds it, when the page has it. */
  heldBy?: Landing
  /** What is picked, in order, and its own place there, from 1. */
  picked?: readonly Proposal[]
  pick?: number | null
  onPick?: () => void
  /** It waits at its review for a person's verdict (Poulpe-688). */
  awaiting?: boolean
}

export function ReadyCard({ issue, proposal, copy, overlaps, merge = null, heldBy, picked = [], pick = null, onPick, awaiting = false }: ReadyCardProps) {
  const { labels, me, issues, proposals } = useBoardState()
  const [open, setOpen] = useState(false)
  const { hash, key } = useLocation()
  const named = hash === `#${proposalAnchor(proposal.id)}`
  useEffect(() => { if (named) setOpen(true) }, [named, key])
  const diffId = useId()
  const root = useRef<HTMLElement>(null)
  const shownFor = useRef<string | null>(null)
  const review = proposal.review
  const reviewer = review ? actorName(review.reviewer) : null
  const engine = review && review.reviewer.kind === 'agent' ? ENGINES[review.reviewer.agent.vendor] : undefined
  const member = me?.member?.id
  const looked = Boolean(member && proposal.looked_at.some((l) => l.member === member && l.head === proposal.head))
  const changes = copy?.changes ?? null
  const keyOf = (id: string) => {
    const of = proposals.get(id)
    return of ? keysText(carriedKeys(of, issues)) : 'Another'
  }
  const held = proposal.held_by !== null
  // Its diff opens once its copy arrives, taller than the card was scrolled to (BoardPage): its top is brought back into view, once for the link.
  const drawn = open && changes !== null
  useEffect(() => {
    if (!named || !drawn || shownFor.current === key) return
    shownFor.current = key
    root.current?.scrollIntoView?.({ block: 'start' })
  }, [named, drawn, key])
  // A batch's anchor is its strip's (ApprovalStrip).
  return <article ref={root} className={cx('card ready-card', !merge && overlaps.length > 0 && 'is-overlap', open && 'is-open', pick !== null && 'is-picked', merge && 'is-merging', held && !merge && 'is-held')}
    id={merge ? undefined : proposalAnchor(proposal.id)} data-board-card={issue.id} aria-label={`${issue.key} ${issue.title}`}>
    {merge ? <span className="ready-pick" title={inMergeText(merge)}><MergeIcon size={14} /></span>
      : awaiting ? <span className="ready-pick" aria-hidden="true" />
      : <label className="ready-pick">
        <input type="checkbox" checked={pick !== null} onChange={onPick} disabled={!onPick} aria-label={`Pick ${issue.key}`} />
        {pick !== null && <span className="ready-pick-order" aria-label={`picked ${pick}`}>{pick}</span>}
      </label>}
    <Link className="card-link" to={`/${encodeURIComponent(issue.key)}`}><CardBody issue={issue} labels={labels} me={me} /></Link>
    {proposal.issue_ids.length > 1 && <p className="ready-carries">Also carries <CarriedIssues proposal={proposal} skipFirst /></p>}
    <div className="ready-proof">
      <UrgencyBadge proposal={proposal} />
      <span className="ready-ok"><CheckIcon size={12} />Checked</span>
      {reviewer ? <span className="ready-ok"><CheckIcon size={12} />Reviewed by <span data-engine={engine}>{reviewer}</span></span>
        : <span className="ready-awaiting">Awaiting your review</span>}
      {looked && <span className="ready-ok ready-you"><CheckIcon size={12} />Looked at by you</span>}
      <span className="ready-stat">
        {changes ? <Counts changes={changes} />
          : copy?.preparing || !copy ? <><Working label="Preparing its diff" aria-hidden /><span>{copy ? 'Preparing its diff…' : 'Reading its diff…'}</span></>
          : <span className="ready-stat-problem" title={copy.problem ?? undefined}>Its diff could not be read</span>}
      </span>
      {!merge && <button type="button" className={cx('ready-diff-toggle', open && 'is-open')} aria-expanded={open} aria-controls={open ? diffId : undefined}
        aria-label={`Diff of ${issue.key}`} disabled={!changes} onClick={() => setOpen((was) => !was)}>
        {open ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}Diff
      </button>}
    </div>
    {merge && <LiveChecks landing={merge.id} />}
    {!merge && overlaps.map((overlap) => {
      const other = keysText(carriedKeys(overlap.with, issues))
      const pickedToo = picked.some((p) => p.id === overlap.with.id)
      return <p key={overlap.with.id} className="ready-overlap"><b>May conflict with {other}</b>{pickedToo && ', which you picked,'} in {overlap.paths.map((path, i) => <span key={path}>{i > 0 && ', '}<code>{path}</code></span>)}</p>
    })}
    {!merge && held && proposal.check_output.length > 0 && <FailedOutput output={proposal.check_output} />}
    {!merge && open && changes && <ReadyDiff id={diffId} issue={issue} proposal={proposal} changes={changes} reviewer={reviewer} looked={looked} awaiting={awaiting} />}
    {merge ? <div className="card-live ready-live"><span className="card-live-text">{inMergeText(merge)}</span></div>
      : held ? <div className="card-live ready-live is-held"><span className="ready-dot" aria-hidden="true" /><span className="card-live-text">{heldText(heldBy, keyOf)}</span></div>
      : <div className="card-live ready-live"><span className="ready-dot" aria-hidden="true" /><span className="card-live-text">{awaiting ? 'Awaiting your review' : 'Ready to merge'} · proposed {longAge(proposal.created_at, Date.now())}</span></div>}
  </article>
}

/** What the failed part of its check said, folded until opened. It is the runner's text: shown as text. */
function FailedOutput({ output }: { output: readonly CheckOutput[] }) {
  return <div className="ready-output">{output.map((part) => <details key={part.part}>
    <summary>Check output · {part.part}{part.truncated && ', the end of it'}</summary>
    <pre tabIndex={0}>{part.tail}</pre>
  </details>)}</div>
}

/** The kept copy's files and diffs, the review's evidence, and the person's own review. */
function ReadyDiff({ id, issue, proposal, changes, reviewer, looked, awaiting }: { id: string; issue: Issue; proposal: Proposal; changes: SessionChanges; reviewer: string | null; looked: boolean; awaiting: boolean }) {
  const { api } = useSessionsEnv()
  const { me } = useBoardState()
  const pathOf = useSessionPath()
  const as = me?.member?.id ?? null
  const read = useCallback((path: string) => api.proposalChangesDiff(proposal.id, path, as), [api, proposal.id, as])
  const first = changes.files.find((file) => file.has_diff && !file.binary)
  const view = useFileDiffs(changes, read, first ? [first.path] : [])
  const verdict = proposal.review?.verdict
  return <div id={id} className="ready-diff">
    <div className="changes-head">
      <span className="changes-summary"><Counts changes={changes} />
        <span className="ready-diff-commits">base <code>{short(changes.base)}</code> · head <code>{short(proposal.head)}</code></span></span>
      <Link className="button button-small button-quiet" to={pathOf(proposal.session_id)}>Open its session</Link>
    </div>
    {changes.files.length === 0 ? <p className="changes-diff-said">No file changed.</p> : <ChangedFiles files={changes.files} view={view} />}
    {changes.truncated && <p className="changes-truncated">More files changed than are listed here.</p>}
    <div className="ready-foot">
      {reviewer && verdict ? <p className="ready-evidence">{reviewer} reviewed it: <b>{verdict.outcome}</b>, {verdict.findings.length === 0 ? 'no findings' : verdict.findings.length === 1 ? '1 finding' : `${verdict.findings.length} findings`} · targeted check passed</p>
        : <p className="ready-evidence">No agent reviews it: your verdict is its review, for head <code>{short(proposal.head)}</code> · targeted check passed</p>}
      <YourReview issue={issue} proposal={proposal} looked={looked} awaiting={awaiting} />
    </div>
  </div>
}

/**
 * Looks good, once per head; or Ask for changes…, which opens a note for its
 * session. Awaiting the person's verdict (Poulpe-688), Approve and Ask for
 * changes… are that verdict, for the exact head shown.
 */
function YourReview({ issue, proposal, looked, awaiting }: { issue: Issue; proposal: Proposal; looked: boolean; awaiting: boolean }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const noteId = useId()
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const lookGood = async () => {
    if (busy) return
    setBusy(true)
    const outcome = awaiting ? await store.reviewVerdict(proposal.id, proposal.head, 'land') : await store.lookAtProposal(proposal.id)
    if (outcome.ok && awaiting) toast(`Approved ${issue.key}.`)
    if (!outcome.ok) toast(outcome.message, 'error')
    if (mounted.current) setBusy(false)
  }
  const send = async (event: FormEvent) => {
    event.preventDefault()
    if (busy || !note.trim()) return
    setBusy(true)
    const outcome = awaiting
      ? await store.reviewVerdict(proposal.id, proposal.head, 'land_with_changes', note.trim())
      : await store.returnProposal(proposal.id, note.trim())
    // Returned, the proposal ends and its card leaves the filter.
    if (outcome.ok) toast(`Sent ${issue.key} back to its session.`)
    else toast(outcome.message, 'error')
    if (mounted.current) setBusy(false)
  }
  if (asking) return <form className="ready-ask" onSubmit={(event) => void send(event)}>
    <label className="visually-hidden" htmlFor={noteId}>What its session should change</label>
    <textarea id={noteId} value={note} onChange={(event) => setNote(event.target.value)} rows={3} maxLength={RETURN_NOTE_MAX}
      placeholder="What should its session change?" autoFocus disabled={busy} />
    <div className="ready-ask-actions">
      <button type="button" className="button button-small button-quiet" onClick={() => { setAsking(false); setNote('') }} disabled={busy}>Cancel</button>
      <button type="submit" className="button button-small button-primary" disabled={busy || offline || !note.trim()} {...needsServer(offline)}>Send to its session</button>
    </div>
  </form>
  return <div className="ready-actions">
    <button type="button" className="button button-small" onClick={() => setAsking(true)} disabled={busy}>Ask for changes…</button>
    {(awaiting || !looked) && <button type="button" className="button button-small button-primary" onClick={() => void lookGood()} disabled={busy || offline} {...needsServer(offline)}><CheckIcon size={12} />{awaiting ? 'Approve' : 'Looks good'}</button>}
  </div>
}
